import { Injectable, OnInit } from "@angular/core";
import { ApiBaseService } from "./api-base.service";
import { DocumentsService } from "./documents.service";
import { AppGlobals } from "../app.globals";
import { AppConfig } from "../app.config";

@Injectable({
  providedIn: "root",
})
export class FileViewerService implements OnInit {
  private accessToken: string | null = null;

  constructor(
    private apiService: ApiBaseService,
    private documentsService: DocumentsService,
  ) {
    this.documentsService
      .getUserDocumentAccessToken({})
      .then((result) => {
        if (!result || result.hasError || !result.accessToken) {
          throw new Error(result?.feedback || "Access token fetch failed");
        }
        this.accessToken = result.accessToken;
      })
      .finally(() => {
        this.tokenPromise = null; // reset
      });
  }

  ngOnInit(): void {}

  /**
   * Fetches a file as a Blob without opening a viewer or triggering a download.
   * Use this to programmatically access file content (e.g. to pre-attach a file).
   */
  async fetchFileBlob(
    fileType: string,
    fileId: string | number,
    fileName: string,
    fileUrl: string = '',
  ): Promise<Blob | null> {
    try {
      await this.ensureAccessToken();
      const apiUrl = this.buildFileUrl(fileType, fileId, fileName, fileUrl);
      const blob: Blob = await this.apiService.httpGETRequest(apiUrl, {}, false, 'blob');
      return blob ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Main entry point. Always opens the FileViewerPage; if the page cannot
   * render the file it shows a "cannot be viewed" message with a download button.
   */
  async openOrDownloadFile(
    fileType: string,
    fileId: string | number,
    fileName: string,
    fileUrl: string,
    displayName?: string,
  ): Promise<void> {
    try {
      const effectiveName = displayName ?? fileName;
      const viewerUrl = this.buildViewerUrl(
        fileType,
        fileId,
        effectiveName,
        fileUrl,
      );
      window.open(viewerUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error in openOrDownloadFile:", error);
      AppGlobals.ShowErrorMessage("Unable to open file.");
    }
  }

  private tokenPromise: Promise<void> | null = null;

  private async ensureAccessToken(): Promise<void> {
    if (this.accessToken) return;

    // Avoid multiple parallel calls
    if (!this.tokenPromise) {
      this.tokenPromise = this.documentsService
        .getUserDocumentAccessToken({})
        .then((result) => {
          if (!result || result.hasError || !result.accessToken) {
            throw new Error(result?.feedback || "Access token fetch failed");
          }
          this.accessToken = result.accessToken;
        })
        .finally(() => {
          this.tokenPromise = null; // reset
        });
    }

    return this.tokenPromise;
  }
  /**
   * Build API endpoint to fetch file content
   */
  private buildFileUrl(
    type: string,
    id: string | number,
    name: string,
    url: string,
  ): string {
    const encodedName = encodeURIComponent(name);
    const encodedUrl = encodeURIComponent(url);
    return `DownloadService/GetFileContent?type=${type}&id=${id}&name=${encodedName}&token=${this.accessToken}&url=${encodedUrl}`;
  }

  /**
   * Build the FileViewer page URL for opening in a new tab
   */
  private buildViewerUrl(
    type: string,
    id: string | number,
    name: string,
    url: string,
  ): string {
    const encodedName = encodeURIComponent(name);
    const encodedUrl = encodeURIComponent(url);
    return `${AppConfig.SiteBase}/file-viewer?fileType=${type}&fileId=${id}&fileName=${encodedName}&fileUrl=${encodedUrl}`;
  }
}

